import L from 'leaflet'
import {
  LAYER_COLOURS,
  TERRITORY_COORDS,
  getCityCoords,
  makeDivIcon,
  makeCountIcon,
} from './territory-map-helpers'
import type { InquiryPin, LayerName, MapData, FanDemandPin } from './territory-map-helpers'

type WithinFn = (lat: number, lng: number) => boolean

/** Render broker pins on the map */
export function renderBrokers(
  map: L.Map,
  data: MapData,
  active: Set<LayerName>,
  within: WithinFn,
): void {
  if (!active.has('brokers') || !data.brokers) return
  const icon = makeDivIcon(LAYER_COLOURS.brokers, 'pin')
  for (const b of data.brokers) {
    const base = TERRITORY_COORDS[b.territory]
    if (!base) continue
    const lat = base[0] + (Math.random() - 0.5) * 5
    const lng = base[1] + (Math.random() - 0.5) * 5
    if (!within(lat, lng)) continue
    L.marker([lat, lng], { icon })
      .addTo(map)
      .bindPopup(
        `<strong>${b.name}</strong><br/>${b.company ?? ''}<br/>`
        + `<em>${b.territory.replace(/_/g, ' ')}</em> &middot; ${b.tier}<br/>`
        + `Active deals: ${b.activeDeals}<br/>`
        + (b.sentiment ? `Sentiment: ${b.sentiment}` : ''),
      )
  }
}

/** Render inquiry density circles on the map */
export function renderInquiries(
  map: L.Map,
  data: MapData,
  active: Set<LayerName>,
  within: WithinFn,
): void {
  if (!active.has('inquiries') || !data.inquiries) return
  const groups: Record<string, { coords: [number, number]; items: InquiryPin[] }> = {}
  for (const inq of data.inquiries) {
    const coords = getCityCoords(inq.city, inq.country)
    if (!coords || !within(coords[0], coords[1])) continue
    const key = `${inq.city}|${inq.country}`
    if (!groups[key]) groups[key] = { coords, items: [] }
    groups[key]!.items.push(inq)
  }
  for (const g of Object.values(groups)) {
    const n = g.items.length
    L.circleMarker(g.coords, {
      radius: Math.min(Math.max(n * 3, 6), 30),
      color: LAYER_COLOURS.inquiries,
      fillColor: LAYER_COLOURS.inquiries,
      fillOpacity: 0.4 + Math.min(n * 0.05, 0.4),
      weight: 2,
    })
      .addTo(map)
      .bindPopup(
        `<strong>${g.items[0]!.city}, ${g.items[0]!.country}</strong><br/>`
        + `${n} inquir${n === 1 ? 'y' : 'ies'}<br/>`
        + g.items
            .slice(0, 5)
            .map((i) => `${i.type} &middot; ${i.stage}`)
            .join('<br/>')
        + (n > 5 ? `<br/><em>+${n - 5} more</em>` : ''),
      )
  }
}

/** Render show stars on the map */
export function renderShows(
  map: L.Map,
  data: MapData,
  active: Set<LayerName>,
  within: WithinFn,
): void {
  if (!active.has('shows') || !data.shows) return
  const icon = makeDivIcon(LAYER_COLOURS.shows, 'star')
  for (const s of data.shows) {
    const coords = getCityCoords(s.city, s.country)
    if (!coords || !within(coords[0], coords[1])) continue
    const lat = coords[0] + (Math.random() - 0.5) * 0.3
    const lng = coords[1] + (Math.random() - 0.5) * 0.3
    const d = new Date(s.date).toLocaleDateString()
    L.marker([lat, lng], { icon })
      .addTo(map)
      .bindPopup(
        `<strong>${s.title}</strong><br/>${s.venue}<br/>`
        + `${s.city}, ${s.country}<br/>${d} &middot; ${s.stage}<br/>`
        + (s.tourName ? `Tour: ${s.tourName}<br/>` : '')
        + (s.legName ? `Leg: ${s.legName}` : ''),
      )
  }
}

/** Render crew person pins on the map */
export function renderCrew(
  map: L.Map,
  data: MapData,
  active: Set<LayerName>,
  within: WithinFn,
): void {
  if (!active.has('crew') || !data.crew) return
  const icon = makeDivIcon(LAYER_COLOURS.crew, 'person')
  for (const c of data.crew) {
    let lat = c.lat
    let lng = c.lng
    if (lat == null || lng == null) {
      const fb = getCityCoords(c.baseCity, c.baseCountry)
      if (!fb) continue
      lat = fb[0]
      lng = fb[1]
    }
    if (!within(lat, lng)) continue
    L.marker([lat, lng], { icon })
      .addTo(map)
      .bindPopup(
        `<strong>${c.name}</strong><br/>${c.primaryRole.replace(/_/g, ' ')}<br/>`
        + `${c.baseCity}, ${c.baseCountry}`,
      )
  }
}

/** Render venue building pins on the map */
export function renderVenues(
  map: L.Map,
  data: MapData,
  active: Set<LayerName>,
  within: WithinFn,
): void {
  if (!active.has('venues') || !data.venues) return
  const icon = makeDivIcon(LAYER_COLOURS.venues, 'building')
  for (const v of data.venues) {
    if (v.lat == null || v.lng == null) continue
    if (!within(v.lat, v.lng)) continue
    L.marker([v.lat, v.lng], { icon })
      .addTo(map)
      .bindPopup(
        `<strong>${v.name}</strong><br/>${v.city}, ${v.country}<br/>`
        + `Tier: ${v.tier}<br/>`
        + (v.capacityMax ? `Capacity: ${v.capacityMax.toLocaleString()}` : ''),
      )
  }
}

/** Render aggregated fan concert-request pins (Velvet Rope social) */
export function renderFanDemand(
  map: L.Map,
  data: MapData,
  active: Set<LayerName>,
  within: WithinFn,
): void {
  if (!active.has('fan_demand') || !data.fan_demand) return
  for (const pin of data.fan_demand as FanDemandPin[]) {
    if (!within(pin.lat, pin.lng)) continue
    const icon = makeCountIcon(LAYER_COLOURS.fan_demand, pin.requestCount)
    const samples = pin.sampleComments
      .map(
        (c) =>
          `&ldquo;${c.text.replace(/</g, '&lt;')}&rdquo;`
          + (c.commenterName ? ` &mdash; ${c.commenterName}` : ''),
      )
      .join('<br/>')
    L.marker([pin.lat, pin.lng], { icon })
      .addTo(map)
      .bindPopup(
        `<strong>${pin.city}${pin.country ? `, ${pin.country}` : ''}</strong><br/>`
        + `<b>${pin.requestCount}</b> fan${pin.requestCount === 1 ? '' : 's'} asked for a show here<br/>`
        + (samples ? `<hr style="margin:6px 0"/>${samples}` : ''),
      )
  }
}
